import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { useMutation } from '@apollo/client';
import { getInputs } from './content';
import { IDescriptionOfRiskDrawerProps } from '.';
import { updateDescriptionOfRisk } from './queries';
import { getError } from '@utils/graph-utils';
import { isEmpty } from '@utils/validationUtils';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';

const DescriptionOfRiskDrawer: React.FC<IDescriptionOfRiskDrawerProps> = ({
  open,
  onClose,
  onSuccess,
  data,
  lovs,
}) => {
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();
  const [saveDescriptionOfRisk] = useMutation(updateDescriptionOfRisk());

  const inputForm = useMemo(
    () => getInputs(lovs, data),
    [lovs, data, data?.LineId?.ExternalCode]
  );

  const submitForm = async (values: Record<string, any>) => {
    setFormDisabled(true);
    setSubmitButtonState('loading');
    try {
      const [formData] = normalizeDynamicFormDrawerValues(
        getInputs(lovs, data),
        values
      );

      const variables = {
        entityId: data?.Id,
        descriptionOfRisk: formData.descriptionOfRisk || null,
        geoLocation: formData.geoLocation || null,
      };

      const { errors } = await saveDescriptionOfRisk({ variables });
      if (isEmpty(errors)) {
        toast.success(
          <ToastSuccessMessage>
            Description of Risk updated successfully!
          </ToastSuccessMessage>
        );
        setSubmitButtonState('success');
        onSuccess();
        onClose();
      } else {
        setSubmitButtonState(undefined);
        const errorMessage = getError({ errors });
        toast.error(<ToastErrorMessage>{errorMessage}</ToastErrorMessage>);
      }
    } catch (error) {
      const errorMessage = getError({ error });
      toast.error(<ToastErrorMessage>{errorMessage}</ToastErrorMessage>);
      setSubmitButtonState(undefined);
    } finally {
      setFormDisabled(false);
    }
  };

  return (
    <DynamicFormDrawer
      title="Modify Description of Risk"
      isOpen={!!open}
      onClose={onClose}
      loading={!data}
      sections={inputForm}
      onSubmit={submitForm}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
    />
  );
};

export default DescriptionOfRiskDrawer;
